// src/pages/portfolio/PortfolioDetailPage.js
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetchPortfolioItem } from '../../services/api';
import { useLang } from '../../i18n/LanguageContext';
import { LoadingSpinner, ErrorBox } from '../../components/ui';
import CandleSVG from '../../components/candles/CandleSVG';
import styles from './PortfolioDetailPage.module.css';

export default function PortfolioDetailPage() {
  const { slug } = useParams();
  const { t, ui } = useLang();
  const { data: item, isLoading, isError, refetch } = useQuery(
    ['portfolioItem', slug],
    () => fetchPortfolioItem(slug),
    { staleTime: 1000 * 60 * 5 }
  );

  if (isLoading) return <div style={{ paddingTop: 'calc(var(--nav-height) + 4rem)' }}><LoadingSpinner /></div>;
  if (isError) return <div style={{ paddingTop: 'calc(var(--nav-height) + 4rem)' }}><ErrorBox onRetry={refetch} /></div>;
  if (!item) return null;

  const specs = [
    item.materials_fa && { label: ui('مواد اولیه', 'Materials'), value: t(item, 'materials') },
    item.dimensions_fa && { label: ui('ابعاد', 'Dimensions'), value: t(item, 'dimensions') },
    item.burn_time_fa && { label: ui('مدت احتراق', 'Burn Time'), value: t(item, 'burn_time') },
  ].filter(Boolean);

  return (
    <main className={styles.main}>
      <Link to="/portfolio" className={styles.back}>
        {ui('← بازگشت به نمونه‌کارها', '← Back to Portfolio')}
      </Link>

      <div className={styles.grid}>
        {/* Visual */}
        <div className={styles.visual} style={{ '--color': item.color }}>
          {item.image
            ? <img src={item.image} alt={t(item, 'title')} className={styles.photo} />
            : (
              <div className={styles.svgWrap}>
                <CandleSVG shape={item.candle_shape} color={item.color} size={200} />
              </div>
            )
          }
          {item.category && (
            <span className={styles.catBadge} style={{ background: item.category.color }}>
              {t(item.category, 'name')}
            </span>
          )}
        </div>

        {/* Info */}
        <div className={styles.info}>
          <h1 className={`${styles.title} grad-text`}>{t(item, 'title')}</h1>
          <p className={styles.desc}>{t(item, 'description')}</p>
          {t(item, 'long_description') && (
            <p className={styles.longDesc}>{t(item, 'long_description')}</p>
          )}

          {specs.length > 0 && (
            <div className={styles.specs}>
              <h3 className={styles.specTitle}>{ui('مشخصات', 'Specifications')}</h3>
              {specs.map(({ label, value }) => (
                <div key={label} className={styles.specRow}>
                  <span className={styles.specLabel}>{label}</span>
                  <span className={styles.specValue}>{value}</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.ctas}>
            <a
              href={`https://t.me/cutecandels`}
              target="_blank" rel="noreferrer"
              className="btn-primary"
            >
              📨 {ui('سفارش از تلگرام', 'Order via Telegram')}
            </a>
            <a
              href={`https://eitaa.com/cutecandels`}
              target="_blank" rel="noreferrer"
              className="btn-secondary"
            >
              📱 {ui('سفارش از ایتا', 'Order via Eitaa')}
            </a>
          </div>
        </div>
      </div>

      {/* Gallery */}
      {item.gallery?.length > 0 && (
        <div className={styles.gallery}>
          <h3 className={styles.galleryTitle}>{ui('گالری تصاویر', 'Image Gallery')}</h3>
          <div className="scroll-track">
            {item.gallery.map((url, i) => (
              <img key={i} src={url} alt={`${t(item, 'title')} ${i + 1}`} className={styles.galleryImg} />
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
